/**
 * Regiment Model
 * 
 * Represents a user's weekly training regiment generated based on assessment performance.
 * Part of the new Regiment Service that works alongside the existing recommended training system.
 */

/**
 * Training type identifiers matching AppEnum naming conventions
 */
export type TrainingType = 'braingame' | 'yahootie' | 'clubhouse' | 'happyplace' | 'outsidetraining';

/**
 * Overall assessment performance level used for global training adjustments
 */
export enum AssessmentLevel {
  High = 'High',
  Average = 'Average',
  Low = 'Low'
}

/**
 * Subscore categories tracked from assessment (excluding Wisdom)
 */
export enum SubscoreCategory {
  Focus = 'Focus',
  Speed = 'Speed',
  Memory = 'Memory',
  Strategy = 'Strategy'
}

/**
 * Individual training recommendation for a specific training type
 */
export interface TrainingRecommendation {
  /** Training type identifier */
  trainingType: TrainingType;
  /** Display name for UI */
  displayName: string;
  /** Recommended sessions per week */
  sessionsPerWeek: number;
  /** Base sessions before any adjustments */
  baselineSessions: number;
  /** Adjustment applied (-1, 0, or +1) */
  adjustment: number;
  /** Icon path for the app */
  iconPath: string;
  /** Array tracking which sessions are completed (index = session number) */
  sessionsCompleted: boolean[];
}

/**
 * Flag indicating a low-performing subscore area
 */
export interface SubscoreFlag {
  /** The subscore category */
  category: SubscoreCategory;
  /** The raw score value from assessment */
  score: number;
  /** Whether this subscore is below the overall average */
  isBelowAverage: boolean;
}

/**
 * Weekly regiment object stored per user
 */
export interface Regiment {
  /** Unique identifier */
  id?: string;
  /** User ID this regiment belongs to */
  userId: string;
  /** Start date of the week this regiment applies to */
  weekStartDate: Date | string;
  /**
   * Local calendar day (YYYY-MM-DD) of the last time we recorded a session completion,
   * tracked per training type. Used to enforce "at most one session completion per day per app".
   */
  lastSessionCompletedDayByTrainingType?: Partial<Record<TrainingType, string>>;
  /** Array of training recommendations for the week */
  trainingRecommendations: TrainingRecommendation[];
  /** Overall assessment performance level */
  overallAssessmentLevel: AssessmentLevel;
  /** Overall average score (excluding Wisdom) */
  overallAverageScore: number;
  /** Flags for subscores that are below average (focus areas) */
  subscoreFlags: SubscoreFlag[];
  /** Timestamp when this regiment was generated */
  generatedAt: Date | string;
  /** Created date (for DB) */
  created_date?: string;
  /** Modified date (for DB) */
  modified_date?: string;
}

/**
 * Per-user regiment storage document
 */
export interface UserRegiments {
  /** Document ID (same as userId) */
  id: string;
  /** User ID */
  userId: string;
  /** Append-only array of weekly regiments */
  weeklyRegiments: Regiment[];
  /** Created date */
  created_date?: string;
  /** Modified date */
  modified_date?: string;
}

/**
 * Assessment report structure (subset of what we need)
 * Based on the report object returned from assessment calculations
 */
export interface AssessmentReportSubscore {
  /** The numeric score value */
  number: number;
  /** Display string (e.g., "10.0") - NOTE: this is a STRING not a number */
  display: string;
  /** Percentage value */
  percent?: number;
  /** Less-than percentage threshold */
  lt_percent?: number;
  /** Description (e.g., "Excellent", "Good", etc.) */
  description?: string;
  [key: string]: any;
}

export interface AssessmentReport {
  /** Overall score (numeric) */
  overall: number;
  speed: AssessmentReportSubscore;
  focus: AssessmentReportSubscore;
  memory: AssessmentReportSubscore;
  strategy: AssessmentReportSubscore;
  wisdom: AssessmentReportSubscore;
  date?: string;
  [key: string]: any;
}

/**
 * Test profile for simulating different user assessment scenarios
 */
export interface TestProfile {
  /** Profile identifier */
  id: string;
  /** Display name */
  name: string;
  /** Description of the profile */
  description: string;
  /** Simulated assessment scores */
  scores: {
    focus: number;
    speed: number;
    memory: number;
    strategy: number;
    wisdom: number;
  };
}
